import { Button, Drawer, Grid2, Menu, MenuItem, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFormContext } from 'react-hook-form';
import { withStyles } from '@mui/styles';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
    ExpenseStatusEnum,
    ExpenseVaultStatusEnum,
    NonClinicianReimbursementFeatureFlagFilteredStatusValuesEnum,
} from './ExpenseStatus.enum';
import {
    FeatureFlag,
    checkFeatureFlag,
} from '../../../components/GlobalComponents/FeatureFlagComponent/FeatureFlag';
import { COLORS } from '../../../utils/Application_Constants';
import { ExpenseBillTypeEnum } from './BillType.enum';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import ExpenseFilterCheckbox from './ExpenseFilterCheckbox';
import ExpenseFilterDate from './ExpenseFilterDate';
import FilterAltIcon from '../../../assets/icons/FilterAltIcon';
import { PreCodedStatusEnum } from './PreCodedStatus.enum';
import { config } from './ExpenseReviewConfig';
import getCriteriaFromLocalStorage from './GetCriteriaFromLocalStorage';
import { getNeedsReviewReasons } from '../../Services/ExpenseService';
import useDebounce from '../../../utils/useDebounce';
import { CreditCardCategoryEnum, CreditCardCategoryIdEnum } from './CreditCardCategory.enum';

// Build the category mapping once at module level for better performance
const CREDIT_CARD_CATEGORY_TO_ID_MAP = Object.entries(CreditCardCategoryIdEnum).reduce(
    (acc, [key, value]) => {
        const categoryName = CreditCardCategoryEnum[value];
        if (categoryName) {
            acc[categoryName] = String(value);
        }
        return acc;
    },
    {}
);

/**
 * Transforms credit card account search queries from category names to numeric IDs.
 *
 * Note: The caller is responsible for invoking this only in the Credit Card context.
 *
 * Examples:
 * - "CVO" → "1"
 * - "CVO-Test" → "1-Test"
 * - "Travel-Corp-ABC123" → "2-ABC123"
 *
 * @param {string} searchQuery - The search query to transform
 * @returns {string} Transformed query for credit cards, original query for others
 */
const transformCreditCardSearch = (searchQuery) => {
    // Check for exact category match (e.g., "CVO" → "1")
    if (CREDIT_CARD_CATEGORY_TO_ID_MAP[searchQuery]) {
        const result = CREDIT_CARD_CATEGORY_TO_ID_MAP[searchQuery];
        return result;
    }

    // Check for category prefix with additional text (e.g., "CVO-Test" → "1-Test")
    for (const [categoryName, categoryId] of Object.entries(CREDIT_CARD_CATEGORY_TO_ID_MAP)) {
        if (searchQuery.startsWith(`${categoryName}-`)) {
            const suffix = searchQuery.substring(categoryName.length + 1);
            const result = `${categoryId}-${suffix}`;
            return result;
        }
    }

    return searchQuery;
};

const styles = () => ({
    formControl: {
        marginBottom: '0',
        minWidth: '215px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '6px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
        },
        '& $disabled': {
            borderRadius: '6px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            background: COLORS.LT_MIDNIGHTBG2,
            color: COLORS.LT_MIDNIGHT33,
        },
        borderRadius: '6px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
    inputRoot: {
        '&$disabled $notchedOutline': {
            borderColor: 'transparent',
        },

        '&$disabled $input': {
            background: COLORS.LT_MIDNIGHTBG2,
            color: COLORS.LT_MIDNIGHT33,
        },
    },
    disabled: {},
    notchedOutline: {},
    input: {
        border: `1px solid ${COLORS.LT_MIDNIGHT5}`,
        borderRadius: '6px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        padding: '8px 16px !important',
        height: 'auto',
        fontSize: '14px',
        lineHeight: '20px',
        '&$disabled': {
            background: COLORS.LT_MIDNIGHTBG2,
        },
    },
    searchKeyButton: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        height: '37px',
        minWidth: '130px',
        justifyContent: 'space-between',
    },
    searchHelperText: {
        position: 'absolute',
        bottom: '-24px',
        marginLeft: 0,
    },
    filterSpacing: {
        paddingTop: '2em',
    },
    title: {
        padding: '2em',
    },
    container: {
        maxHeight: '90em',
        overflow: 'auto',
        paddingLeft: '2em',
        paddingRight: '2em',
        position: 'relative',
        height: '90em',
    },
    header: {
        fontSize: '1.5em',
        color: '#336195',
        padding: '2em',
    },
    exit: {
        marginLeft: '13em',
        marginBottom: '3em',
        cursor: 'pointer',
    },
    filterButton: {
        color: COLORS.LT_JOURNEY,
        background: COLORS.LT_JOURNEY10,
    },
});

const ExpenseFiltersForm = (props) => {
    const {
        classes,
        criteria,
        fetchData,
        setIsLoading,
        setOffset,
        setPage,
        limit,
        sortBy,
        sortDirection,
        expenseCategories,
        creditCardList,
        availableDivisions,
        pendingCCReview,
        expenseClass,
        vaultReview = false,
        forceShowSearch = false,
    } = props;

    const getSearchQuery = (query, currentExpenseClass, currentSearchKey) => {
        if (
            currentExpenseClass === ExpenseClassEnum.CreditCard &&
            currentSearchKey?.value === 'creditCardAccount' &&
            query
        ) {
            return transformCreditCardSearch(query);
        }
        return query || '';
    };

    const flags = useFlags();
    const { ltoeWebExpensesEnableInCorrection } = flags;
    const { watch, getValues, reset, clearErrors } = useFormContext();

    // Dynamic status enum selection
    const getStatusEnum = () => {
        if (vaultReview || expenseClass === ExpenseClassEnum.Vault) {
            return ExpenseVaultStatusEnum;
        }

        const isExpenseNonPreCoded =
            expenseClass === ExpenseClassEnum.CreditCard ||
            expenseClass === ExpenseClassEnum.IngestedCCTransaction ||
            expenseClass === ExpenseClassEnum.Reimbursement ||
            expenseClass === ExpenseClassEnum.Payment ||
            pendingCCReview;

        // It's pre-coded if it's not one of the above
        return isExpenseNonPreCoded ? ExpenseStatusEnum : PreCodedStatusEnum;
    };

    const statusEnum = getStatusEnum();

    const statusList = Object.values(statusEnum).filter((status) =>
        !ltoeWebExpensesEnableInCorrection ? status.value !== 'InCorrection' : true
    );
    const filteredStatusList = statusList.filter((status) => status.value !== 'New');

    const getFilteredBillTypes = () => {
        let billTypes = Object.values(ExpenseBillTypeEnum);

        if (expenseClass !== ExpenseClassEnum.CreditCard) {
            billTypes = billTypes.filter((billType) => billType !== 'SG&A');
        }

        return billTypes;
    };

    const filteredBillTypes = getFilteredBillTypes();
    const divisions = availableDivisions.map((a) => a.description);
    const allFormValues = watch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isNCREFFEnabled, setIsNCREFFEnabled] = useState(false); // Non-Clinician Reimbursement Expense Feature Flag check
    const dateFieldRefs = useRef({});

    const getSearchOptions = () => {
        if (expenseClass === ExpenseClassEnum.Payment) {
            return config.vendorPaymentSearchKeys;
        } else if (expenseClass === ExpenseClassEnum.Vault) {
            return config.vaultSearchKeys;
        } else if (expenseClass === ExpenseClassEnum.CreditCard) {
            return config.creditCardSearchKeys || config.searchKeys;
        }
        return config.searchKeys;
    };

    const searchOptions = getSearchOptions();

    const expenseCategoriesNCREFF = (() => {
        const toReturn = [];

        expenseCategories?.forEach((expenseCategory) => {
            switch (expenseCategory.category) {
                case 'Licensing':
                    toReturn.push({
                        category: expenseCategory.category,
                        types: expenseCategory.types.filter(
                            (catType) =>
                                !['Renewal', 'Re-Application'].includes(catType.description)
                        ),
                    });
                    break;
                case 'Privileging':
                    toReturn.push({
                        category: expenseCategory.category,
                        types: expenseCategory.types.filter(
                            (catType) =>
                                ![
                                    'Application Fee',
                                    'Background Check',
                                    'Shipping',
                                    'Medical Staff Dues',
                                    'Reappointment Fees',
                                    'Lab Fees',
                                    'Fingerprinting',
                                    'Verification',
                                    'Re-Application',
                                ].includes(catType.description)
                        ),
                    });
                    break;
                case 'Travel':
                    toReturn.push({
                        category: expenseCategory.category,
                        types: expenseCategory.types.filter(
                            (catType) => !['Miscellaneous'].includes(catType.description)
                        ),
                    });
                    break;
                case 'Credentialing':
                case 'Other':
                case 'ClientProducer':
                    break;
                default:
                    toReturn.push(expenseCategory);
                    break;
            }
        });

        return toReturn;
    })();
    const divisionsNCREFF = divisions.filter(
        (division) => !['Corporate', 'Customer Care', 'CVO', 'Malpractice'].includes(division)
    );
    const billTypesNCREFF = filteredBillTypes.filter(
        (billType) => !['Clinician Deduction'].includes(billType)
    );

    const countTotalNumFilters = (obj) => {
        let count = 0;
        const dateRanges = new Set([
            'StartStartDate',
            'StartEndDate',
            'EndStartDate',
            'EndEndDate',
            'PostStartDate',
            'PostEndDate',
            'PeriodEndStart',
            'PeriodEndEnd',
            'ImportedStart',
            'ImportedEnd',
            'TransactionStartDate',
            'TransactionEndDate',
            'PayDateStart',
            'PayDateEnd',
            'SubmittedStartDate',
            'SubmittedEndDate',
        ]);
        const dateRangesCounted = new Set();

        Object.keys(obj).forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key];
                if (value !== null && !(Array.isArray(value) && value.length === 0) && !!value) {
                    if (dateRanges.has(key)) {
                        // Get the base name of the date range
                        let baseName = key;
                        if (key.startsWith('PeriodEnd')) {
                            baseName = 'PeriodEnd';
                        } else if (key.startsWith('PayDate')) {
                            baseName = 'PayDate';
                        } else if (key.startsWith('Imported')) {
                            baseName = 'Imported';
                        } else {
                            baseName = key.replace(/(StartDate|EndDate)/, '');
                        }
                        if (!dateRangesCounted.has(baseName)) {
                            dateRangesCounted.add(baseName);
                            count++;
                        }
                    } else {
                        count++;
                    }
                }
            }
        });
        return count;
    };
    const [lastAppliedFilterCount, setLastAppliedFilterCount] = useState(
        countTotalNumFilters(allFormValues)
    );

    const toggleDrawer = (newOpen) => {
        setDrawerOpen(newOpen);
        if (newOpen) {
            const filterValues = getCriteriaFromLocalStorage(expenseClass);

            reset({
                Statuses: filterValues ? filterValues.statuses : [],
                Divisions: filterValues ? filterValues.divisions : [],
                'Expense Types':
                    filterValues && filterValues.expenseTypes ? filterValues.expenseTypes : [],
                'Bill Types': filterValues && filterValues.billTypes ? filterValues.billTypes : [],
                Sources: filterValues && filterValues.sources ? filterValues.sources : [],
                'Needs Review Reasons':
                    filterValues && filterValues.needsReviewReasons
                        ? filterValues.needsReviewReasons
                        : [],
                'Has Receipt': filterValues?.hasReceipt || [],
                'Merchant States':
                    filterValues && filterValues.merchantStates ? filterValues.merchantStates : [],
                StartStartDate:
                    filterValues && filterValues.startStartDate
                        ? filterValues.startStartDate
                        : null,
                StartEndDate:
                    filterValues && filterValues.startEndDate ? filterValues.startEndDate : null,
                EndStartDate:
                    filterValues && filterValues.endStartDate ? filterValues.endStartDate : null,
                EndEndDate:
                    filterValues && filterValues.endEndDate ? filterValues.endEndDate : null,
                PostStartDate:
                    filterValues && filterValues.postStartDate ? filterValues.postStartDate : null,
                PostEndDate:
                    filterValues && filterValues.postEndDate ? filterValues.postEndDate : null,
                PeriodEndStart:
                    filterValues && filterValues.periodEndStart
                        ? filterValues.periodEndStart
                        : null,
                PeriodEndEnd:
                    filterValues && filterValues.periodEndEnd ? filterValues.periodEndEnd : null,
                ImportedStart:
                    filterValues && filterValues.importedStart ? filterValues.importedStart : null,
                ImportedEnd:
                    filterValues && filterValues.importedEnd ? filterValues.importedEnd : null,
                TransactionStartDate:
                    filterValues && filterValues.transactionStartDate
                        ? filterValues.transactionStartDate
                        : null,
                TransactionEndDate:
                    filterValues && filterValues.transactionEndDate
                        ? filterValues.transactionEndDate
                        : null,
                PayDateStart:
                    filterValues && filterValues.payDateStart ? filterValues.payDateStart : null,
                PayDateEnd:
                    filterValues && filterValues.payDateEnd ? filterValues.payDateEnd : null,
                SubmittedStartDate:
                    filterValues && filterValues.submittedStartDate
                        ? filterValues.submittedStartDate
                        : null,
                SubmittedEndDate:
                    filterValues && filterValues.submittedEndDate
                        ? filterValues.submittedEndDate
                        : null,
                'Credit Card': filterValues?.creditCardList ? filterValues.creditCardList : [],
            });
        }
    };
    const states = config.states.map((state) => state.value);

    const [needsReviewReasonsDictionary, setNeedsReviewReasonsDictionary] = useState([]);
    useEffect(() => {
        const fetchNeedsReviewReasons = async () => {
            try {
                const response = await getNeedsReviewReasons();
                setNeedsReviewReasonsDictionary(response);
            } catch (error) {
                console.error(error);
            }
        };
        fetchNeedsReviewReasons();

        checkFeatureFlag('NonClinicianReimbursementExpenseFeatures', false)
            .then((flagValue) => {
                setIsNCREFFEnabled(flagValue);
            })
            .catch((error) => console.error(error));
    }, []);
    const needsReviewReasons = Object.values(needsReviewReasonsDictionary);

    const [search, setSearch] = useState(criteria ? criteria.search : '');
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const getDefaultSearchKey = () => {
        if (expenseClass === ExpenseClassEnum.Payment) {
            return config.vendorPaymentSearchKeys?.[0] || config.searchKeys[0];
        } else if (expenseClass === ExpenseClassEnum.Vault) {
            return config.vaultSearchKeys?.[0] || config.searchKeys[0];
        } else if (expenseClass === ExpenseClassEnum.CreditCard) {
            return config.creditCardSearchKeys?.[0] || config.searchKeys[0];
        }
        return config.searchKeys[0];
    };

    const [searchKey, setSearchKey] = useState(
        criteria && criteria.searchKey ? criteria.searchKey : getDefaultSearchKey()
    );
    const searchRef = useRef(null);

    // Update search key when expense class changes
    useEffect(() => {
        const currentSearchOptions = getSearchOptions();

        // For Credit Card expenses, always preserve the creditCardSearchKeys
        if (expenseClass === ExpenseClassEnum.CreditCard) {
            // Check if we already have a valid credit card search key
            if (
                searchKey &&
                config.creditCardSearchKeys?.some((option) => option.value === searchKey.value)
            ) {
                // Keep the current searchKey if it's valid for credit cards
                return;
            }
            // Otherwise set the first credit card search key
            const creditCardDefaultKey = config.creditCardSearchKeys?.[0] || config.searchKeys[0];
            setSearchKey(creditCardDefaultKey);
            return;
        }

        // For non-credit card expenses, use the original logic
        if (criteria?.searchKey) {
            // Check if the current searchKey exists in the new options
            const searchKeyExists = currentSearchOptions.some(
                (option) => option.value === criteria.searchKey.value
            );

            if (!searchKeyExists) {
                // If the saved searchKey doesn't exist in new options, use default
                const newDefaultSearchKey = getDefaultSearchKey();
                setSearchKey(newDefaultSearchKey);
            } else {
                // Use the saved searchKey
                setSearchKey(criteria.searchKey);
            }
        } else {
            // No saved searchKey, use default
            const newDefaultSearchKey = getDefaultSearchKey();
            setSearchKey(newDefaultSearchKey);
        }
    }, [expenseClass]);

    const handleSearch = async (query) => {
        setIsLoading(true);
        const newOffset = 0;
        const newPage = 1;
        setOffset(newOffset);
        setPage(newPage);
        const filterValues = getCriteriaFromLocalStorage(expenseClass);

        // Ensure we use the correct searchKey for the expense type
        let effectiveSearchKey = searchKey;

        // Special handling for Credit Card expenses to maintain creditCardSearchKeys
        if (filterValues?.expenseClass === ExpenseClassEnum.CreditCard) {
            const validCreditCardKeys = config.creditCardSearchKeys || [];
            const isValidCreditCardKey = validCreditCardKeys.some(
                (key) => key.value === searchKey?.value
            );

            // If current searchKey is not valid for credit cards, use default
            if (!isValidCreditCardKey) {
                effectiveSearchKey = validCreditCardKeys[0] || config.searchKeys[0];
            }
        }

        const request = {
            expenseClass: filterValues?.expenseClass,
            offset: newOffset,
            sortBy,
            sortDirection,
            limit: 50,
            statuses: filterValues ? filterValues.statuses : [],
            divisions: filterValues ? filterValues.divisions : [],
            transactionStartDate:
                filterValues && filterValues.transactionStartDate
                    ? filterValues.transactionStartDate
                    : null,
            transactionEndDate:
                filterValues && filterValues.transactionEndDate
                    ? filterValues.transactionEndDate
                    : null,
            expenseTypes:
                filterValues && filterValues.expenseTypes ? filterValues.expenseTypes : [],
            sources: filterValues && filterValues.sources ? filterValues.sources : [],
            needsReviewReasons:
                filterValues && filterValues.needsReviewReasons
                    ? filterValues.needsReviewReasons
                    : [],
            hasReceipt: filterValues?.hasReceipt || [],
            billTypes: filterValues && filterValues.billTypes ? filterValues.billTypes : [],
            startStartDate:
                filterValues && filterValues.startStartDate ? filterValues.startStartDate : null,
            startEndDate:
                filterValues && filterValues.startEndDate ? filterValues.startEndDate : null,
            endStartDate:
                filterValues && filterValues.endStartDate ? filterValues.endStartDate : null,
            endEndDate: filterValues && filterValues.endEndDate ? filterValues.endEndDate : null,
            postStartDate:
                filterValues && filterValues.postStartDate ? filterValues.postStartDate : null,
            postEndDate: filterValues && filterValues.postEndDate ? filterValues.postEndDate : null,
            periodEndStart:
                filterValues && filterValues.periodEndStart ? filterValues.periodEndStart : null,
            periodEndEnd:
                filterValues && filterValues.periodEndEnd ? filterValues.periodEndEnd : null,
            importedStart:
                filterValues && filterValues.importedStart ? filterValues.importedStart : null,
            importedEnd: filterValues && filterValues.importedEnd ? filterValues.importedEnd : null,
            payDateStart:
                filterValues && filterValues.payDateStart ? filterValues.payDateStart : null,
            payDateEnd: filterValues && filterValues.payDateEnd ? filterValues.payDateEnd : null,
            submittedStartDate:
                filterValues && filterValues.submittedStartDate
                    ? filterValues.submittedStartDate
                    : null,
            submittedEndDate:
                filterValues && filterValues.submittedEndDate
                    ? filterValues.submittedEndDate
                    : null,
            merchantStates:
                filterValues && filterValues.merchantStates ? filterValues.merchantStates : [],
            search: getSearchQuery(
                query,
                expenseClass || filterValues?.expenseClass,
                effectiveSearchKey
            ),
            searchKey: effectiveSearchKey,
            creditCardList: filterValues.creditCardList ? filterValues.creditCardList : [],
        };

        await fetchData(request);
        if (searchRef.current) searchRef.current.focus();
    };

    const debouncedRequest = useDebounce(() => {
        if (search?.length >= 3 && search?.length <= 200) {
            setDebouncedSearch(search);
            handleSearch(search);
        } else if (debouncedSearch !== '') {
            setDebouncedSearch('');
            handleSearch('');
        }
    });

    const onCloseDrawer = () => {
        toggleDrawer(false);
    };

    const onClear = () => {
        reset({
            Statuses: [],
            Divisions: [],
            'Expense Types': [],
            'Bill Types': [],
            Sources: [],
            'Needs Review Reasons': [],
            'Has Receipt': [],
            'Merchant States': [],
            StartStartDate: null,
            StartEndDate: null,
            EndStartDate: null,
            EndEndDate: null,
            PostStartDate: null,
            PostEndDate: null,
            PeriodEndStart: null,
            PeriodEndEnd: null,
            ImportedStart: null,
            ImportedEnd: null,
            TransactionStartDate: null,
            TransactionEndDate: null,
            PayDateStart: null,
            PayDateEnd: null,
            SubmittedStartDate: null,
            SubmittedEndDate: null,
            'Credit Card': [],
        });
        clearErrors();
        Object.values(dateFieldRefs.current).forEach((ref) => {
            ref?.clearDateError?.();
        });
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSearchKey = (key) => {
        setSearchKey(key);
        handleClose();
        // Only search if there's already a search term
        if (debouncedSearch !== '') handleSearch(search);
    };

    const handleFilterApply = async () => {
        setLastAppliedFilterCount(countTotalNumFilters(getValues()));
        setIsLoading(true);
        const newOffset = 0;
        const newPage = 1;
        setOffset(newOffset);
        setPage(newPage);
        const filterValues = getCriteriaFromLocalStorage(expenseClass);
        const request = {
            expenseClass: filterValues?.expenseClass,
            offset: newOffset,
            sortBy,
            sortDirection,
            limit: 50,
            statuses: getValues('Statuses'),
            divisions: getValues('Divisions'),
            transactionStartDate: getValues('TransactionStartDate'),
            transactionEndDate: getValues('TransactionEndDate'),
            expenseTypes: getValues('Expense Types'),
            sources: getValues('Sources'),
            needsReviewReasons: getValues('Needs Review Reasons'),
            hasReceipt: getValues('Has Receipt'),
            billTypes: getValues('Bill Types'),
            startStartDate: getValues('StartStartDate'),
            startEndDate: getValues('StartEndDate'),
            endStartDate: getValues('EndStartDate'),
            endEndDate: getValues('EndEndDate'),
            postStartDate: getValues('PostStartDate'),
            postEndDate: getValues('PostEndDate'),
            periodEndStart: getValues('PeriodEndStart'),
            periodEndEnd: getValues('PeriodEndEnd'),
            importedStart: getValues('ImportedStart'),
            importedEnd: getValues('ImportedEnd'),
            payDateStart: getValues('PayDateStart'),
            payDateEnd: getValues('PayDateEnd'),
            submittedStartDate: getValues('SubmittedStartDate'),
            submittedEndDate: getValues('SubmittedEndDate'),
            merchantStates: getValues('Merchant States'),
            creditCardList: getValues('Credit Card'),
            search: getSearchQuery(
                filterValues?.search,
                expenseClass || filterValues?.expenseClass,
                searchKey
            ),
            searchKey: (() => {
                // For Credit Card expenses, preserve the current searchKey from state
                if (filterValues?.expenseClass === ExpenseClassEnum.CreditCard) {
                    return searchKey;
                }

                // For other expense types, use existing logic
                if (filterValues && filterValues.searchKey) {
                    return filterValues.searchKey;
                }

                if (filterValues?.expenseClass === ExpenseClassEnum.Payment) {
                    return config.vendorPaymentSearchKeys?.[0] || config.searchKeys[0];
                } else if (filterValues?.expenseClass === ExpenseClassEnum.Vault) {
                    return config.vaultSearchKeys?.[0] || config.searchKeys[0];
                }
                return config.searchKeys[0];
            })(),
        };
        toggleDrawer(false);

        await fetchData(request);
    };

    const renderFilterTitle = () => (
        <Grid2 container alignItems="center" spacing={1}>
            <Grid2 item>
                <Typography variant="h5" component="h5" className={classes.header}>
                    Filters ({countTotalNumFilters(allFormValues)})
                </Typography>
            </Grid2>
            <Grid2 item className={classes.exit}>
                <CloseIcon onClick={() => toggleDrawer(false)} />
            </Grid2>
        </Grid2>
    );

    const renderFilterButtons = () => {
        const {
            formState: { errors },
        } = useFormContext();

        const hasErrors = Object.keys(errors).length > 0;
        return (
            <Grid2 container spacing={1} className={classes.title}>
                <Grid2 item>
                    <Button variant="contained" color="grey" onClick={() => onClear()}>
                        Clear All
                    </Button>
                </Grid2>
                <Grid2 item>
                    <Button
                        aria-controls="search-key"
                        aria-haspopup="true"
                        onClick={() => handleFilterApply()}
                        variant="contained"
                        color="primary"
                        id="search-key"
                        disabled={hasErrors}
                    >
                        Apply Filters
                    </Button>
                </Grid2>
            </Grid2>
        );
    };

    const renderDrawer = () => (
        <Drawer
            open={drawerOpen}
            onClose={() => onCloseDrawer()}
            sx={() => ({
                zIndex: 1310,
            })}
        >
            {renderFilterTitle()}
            <Grid2 className={classes.container}>
                <FeatureFlag
                    name="NonClinicianReimbursementExpenseFeatures"
                    isFlagEnabled="true"
                    fallbackValue={false}
                >
                    <ExpenseFilterCheckbox filterName="Statuses" list={filteredStatusList} />
                </FeatureFlag>
                <FeatureFlag
                    name="NonClinicianReimbursementExpenseFeatures"
                    isFlagEnabled="false"
                    fallbackValue={false}
                >
                    <ExpenseFilterCheckbox
                        filterName="Statuses"
                        list={filteredStatusList.filter(
                            (statusToFilter) =>
                                !Object.values(
                                    NonClinicianReimbursementFeatureFlagFilteredStatusValuesEnum
                                ).includes(statusToFilter.value)
                        )}
                    />
                </FeatureFlag>
                {!pendingCCReview && !vaultReview && (
                    <FeatureFlag
                        name="NonClinicianReimbursementExpenseFeatures"
                        isFlagEnabled="true"
                        fallbackValue={false}
                    >
                        <ExpenseFilterDate
                            ref={(el) => {
                                if (el) dateFieldRefs.current.PeriodEnd = el;
                            }}
                            filterName="Period End Date"
                            displayStartDate="PeriodEndStart"
                            displayEndDate="PeriodEndEnd"
                        />
                    </FeatureFlag>
                )}
                {vaultReview && (
                    <ExpenseFilterDate
                        ref={(el) => {
                            if (el) dateFieldRefs.current.Imported = el;
                        }}
                        filterName="Imported Date"
                        displayStartDate="ImportedStart"
                        displayEndDate="ImportedEnd"
                    />
                )}
                {!vaultReview && (
                    <ExpenseFilterDate
                        ref={(el) => {
                            if (el) dateFieldRefs.current.TransactionDate = el;
                        }}
                        filterName="Transaction Date"
                        displayStartDate="TransactionStartDate"
                        displayEndDate="TransactionEndDate"
                    />
                )}
                {!pendingCCReview && !vaultReview && (
                    <FeatureFlag
                        name="NonClinicianReimbursementExpenseFeatures"
                        isFlagEnabled="true"
                        fallbackValue={false}
                    >
                        <ExpenseFilterDate
                            ref={(el) => {
                                if (el) dateFieldRefs.current.PostDate = el;
                            }}
                            filterName="Post Date"
                            displayStartDate="PostStartDate"
                            displayEndDate="PostEndDate"
                        />
                    </FeatureFlag>
                )}
                {!pendingCCReview && !vaultReview && (
                    <FeatureFlag
                        name="NonClinicianReimbursementExpenseFeatures"
                        isFlagEnabled="true"
                        fallbackValue={false}
                    >
                        <ExpenseFilterCheckbox filterName="Merchant States" list={states} />
                    </FeatureFlag>
                )}
                {!vaultReview && (
                    <ExpenseFilterDate
                        ref={(el) => {
                            if (el) dateFieldRefs.current.StartDate = el;
                        }}
                        filterName="Start Date"
                        displayStartDate="StartStartDate"
                        displayEndDate="StartEndDate"
                    />
                )}
                {!vaultReview && (
                    <ExpenseFilterDate
                        ref={(el) => {
                            if (el) dateFieldRefs.current.EndDate = el;
                        }}
                        filterName="End Date"
                        displayStartDate="EndStartDate"
                        displayEndDate="EndEndDate"
                    />
                )}
                {!pendingCCReview && !vaultReview && (
                    <ExpenseFilterDate
                        ref={(el) => {
                            if (el) dateFieldRefs.current.PayDate = el;
                        }}
                        filterName="Pay Date"
                        displayStartDate="PayDateStart"
                        displayEndDate="PayDateEnd"
                    />
                )}
                {!pendingCCReview && !vaultReview && (
                    <ExpenseFilterDate
                        ref={(el) => {
                            if (el) dateFieldRefs.current.SubmittedDate = el;
                        }}
                        filterName="Submitted Date"
                        displayStartDate="SubmittedStartDate"
                        displayEndDate="SubmittedEndDate"
                    />
                )}

                <ExpenseFilterCheckbox
                    filterName="Bill Types"
                    list={isNCREFFEnabled ? filteredBillTypes : billTypesNCREFF}
                />

                <ExpenseFilterCheckbox
                    filterName="Divisions"
                    list={isNCREFFEnabled ? divisions : divisionsNCREFF}
                />

                <ExpenseFilterCheckbox
                    filterName="Expense Types"
                    list={isNCREFFEnabled ? expenseCategories : expenseCategoriesNCREFF}
                    isExpenseType
                />
                {!pendingCCReview && !vaultReview && (
                    <ExpenseFilterCheckbox
                        filterName="Sources"
                        list={isNCREFFEnabled ? config.source : config.sourceNCREFF}
                    />
                )}
                {!pendingCCReview && !vaultReview && (
                    <ExpenseFilterCheckbox
                        filterName="Needs Review Reasons"
                        list={needsReviewReasons}
                    />
                )}
                {vaultReview && (
                    <ExpenseFilterCheckbox filterName="Has Receipt" list={['Yes', 'No']} />
                )}
                {creditCardList && (
                    <ExpenseFilterCheckbox
                        filterName="Credit Card"
                        list={creditCardList
                            .map((cc) => ({
                                label: `${CreditCardCategoryEnum[cc.category]}-${cc.name}-${
                                    cc.lastFourDigits
                                }`,
                                value: cc.id,
                            }))
                            .sort((a, b) => a.label.localeCompare(b.label))}
                    />
                )}
            </Grid2>
            {renderFilterButtons()}
        </Drawer>
    );

    const renderFilters = () => (
        <Grid2 item style={{ flex: 1 }}>
            <Button onClick={() => toggleDrawer(true)} className={classes.filterButton}>
                <FilterAltIcon />
                Filters ({lastAppliedFilterCount})
            </Button>
            {renderDrawer()}
        </Grid2>
    );

    const renderSearch = () => {
        const isIdSearch =
            searchKey?.value === 'clinicianId' ||
            searchKey?.value === 'expenseId' ||
            searchKey?.value === 'clientId';
        let helperText = '';

        if (search && isIdSearch) {
            helperText = 'Complete value required for results';
        } else if (search?.length < 3) {
            helperText = 'Please enter 3 or more characters';
        } else if (search?.length > 200) {
            helperText = 'Must be less than 200 characters';
        }

        return (
            <Grid2 item>
                <Grid2 container>
                    <Grid2 item>
                        <Button
                            aria-controls="search-key"
                            aria-haspopup="true"
                            onClick={handleClick}
                            variant="contained"
                            color="primary"
                            id="search-key"
                            endIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.searchKeyButton}
                        >
                            {searchKey?.name}
                        </Button>
                        <Menu
                            id="search-key-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl?.id === 'search-key')}
                            onClose={handleClose}
                        >
                            {searchOptions.map((key) => (
                                <MenuItem
                                    key={key.name}
                                    selected={key === searchKey}
                                    onClick={() => handleSearchKey(key)}
                                >
                                    {key.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid2>
                    <Grid2 item>
                        <TextField
                            id="search"
                            type="search"
                            variant="outlined"
                            defaultValue={search}
                            onChange={(e) => {
                                setSearch(e.target.value.trim());
                                debouncedRequest();
                            }}
                            className={classes.formControl}
                            InputProps={{
                                classes: {
                                    root: classes.inputRoot,
                                    input: classes.input,
                                    disabled: classes.disabled,
                                    notchedOutline: classes.notchedOutline,
                                },
                            }}
                            inputRef={searchRef}
                            FormHelperTextProps={{ className: classes.searchHelperText }}
                            helperText={helperText}
                            error={Boolean(search && search?.length > 200)}
                            placeholder="Search expenses"
                        />
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    };

    return (
        <Grid2
            container
            justifyContent="space-between"
            spacing={2}
            className={classes.filterSpacing}
        >
            {renderFilters()}
            {(!pendingCCReview || forceShowSearch) && renderSearch()}
        </Grid2>
    );
};

export default withStyles(styles)(ExpenseFiltersForm);
